import { Play, Pause, SkipForward, SkipBack, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAutoplay } from "@/hooks/useAutoplay";

const PACE_PRESETS = [8, 12, 20, 30];

export default function AutoplayBar() {
  const { on, toggle, pace, setPace, progress, currentIdx, total, skip, back } = useAutoplay();

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 no-print"
        role="region"
        aria-label="Autoplay controls"
      >
        <div className="flex items-center gap-2 rounded-full border bg-background/95 backdrop-blur shadow-lg pl-2 pr-1.5 py-1.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={on ? "default" : "ghost"}
                onClick={toggle}
                aria-label={on ? "Pause autoplay" : "Start autoplay"}
                aria-pressed={on}
                className="h-8 rounded-full gap-1.5 px-3"
              >
                {on ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                <span className="text-xs font-medium">{on ? "Playing" : "Autoplay"}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Press <kbd className="px-1 rounded bg-muted">A</kbd> to toggle
            </TooltipContent>
          </Tooltip>

          <div className="hidden sm:flex items-center gap-1 border-l pl-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={back}
                  aria-label="Previous step"
                  className="h-7 w-7 rounded-full"
                >
                  <SkipBack className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Previous · [</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={skip}
                  aria-label="Next step"
                  className="h-7 w-7 rounded-full"
                >
                  <SkipForward className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Next · ]</TooltipContent>
            </Tooltip>
          </div>

          <div className="hidden md:flex items-center gap-1 pl-2 border-l">
            <Timer className="w-3.5 h-3.5 text-muted-foreground" />
            {PACE_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPace(p)}
                aria-pressed={pace === p}
                aria-label={`Set pace to ${p} seconds`}
                className={`text-[11px] px-1.5 py-0.5 rounded-full transition ${
                  pace === p
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {p}s
              </button>
            ))}
          </div>

          <div className="text-[10px] text-muted-foreground tabular-nums pr-1 hidden sm:block">
            {currentIdx + 1}/{total}
          </div>
        </div>

        {on && (
          <div
            className="mt-1.5 mx-3 h-1 rounded-full bg-muted overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
          >
            <div
              className="h-full bg-primary transition-[width] duration-200 ease-linear"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
